'use client'

import type { ModelCatalogDiff, ModelProbeReport } from '@/lib/trpc'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { gameQueries } from '@/lib/queries'
import { AlertTriangle, CheckCircle2, CloudDownload, Loader2, RefreshCw, Sparkles, Trash2, XCircle, Zap } from 'lucide-react'
import { useState } from 'react'

type Selection = Record<string, Set<string>>

const PROVIDER_LABELS: Record<string, string> = {
  'openai': 'OpenAI',
  'google': 'Google',
  'anthropic': 'Anthropic',
  'x-ai': 'xAI',
  'deepseek': 'DeepSeek',
  'z-ai': 'Z-AI (GLM)',
  'moonshotai': 'Moonshot AI',
}

/**
 * Authors routed via their official/direct provider API; everything else routes through OpenRouter.
 * Mirrors DIRECT_PROVIDER_AUTHORS in apps/api/src/services/ai/model-catalog.ts.
 */
const OFFICIAL_PROVIDERS = new Set(['openai', 'google', 'anthropic', 'x-ai'])

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

/** Format a per-token USD price as a per-million-token string. */
function formatPerMillion(pricePerToken: number | null | undefined): string {
  if (pricePerToken === null || pricePerToken === undefined)
    return '—'
  const perMillion = pricePerToken * 1_000_000
  if (perMillion === 0)
    return '$0'
  if (perMillion < 0.01)
    return `$${perMillion.toFixed(4)}`
  return `$${perMillion.toFixed(2)}`
}

function shortId(id: string): string {
  return id.split('/').slice(1).join('/') || id
}

/** One pill summarising a model's probe result on its single production path. */
function ProbePill({ report }: { report?: ModelProbeReport }) {
  if (!report)
    return null
  return (
    <span
      title={report.error ?? `${report.latencyMs}ms`}
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${
        report.ok
          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
          : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
      }`}
    >
      {report.ok ? <CheckCircle2 className="size-3" /> : <XCircle className="size-3" />}
      {report.provider}
      {report.ok ? ` ${report.latencyMs}ms` : ' fail'}
    </span>
  )
}

export function ModelCatalogSection() {
  const [diff, setDiff] = useState<ModelCatalogDiff | null>(null)
  const [selection, setSelection] = useState<Selection>({})
  const [probes, setProbes] = useState<Record<string, ModelProbeReport>>({})
  const [syncing, setSyncing] = useState(false)
  const [probing, setProbing] = useState(false)
  const [applying, setApplying] = useState(false)
  const [refreshResponsive, setRefreshResponsive] = useState(false)
  const [confirmApply, setConfirmApply] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info', text: string } | null>(null)

  const initSelection = (d: ModelCatalogDiff): Selection => {
    const sel: Selection = {}
    for (const [author, cands] of Object.entries(d.byAuthor))
      sel[author] = new Set(cands.filter(c => c.inCurrentConfig || c.suggested).map(c => c.id))
    return sel
  }

  const handleSync = async () => {
    setSyncing(true)
    setMessage(null)
    setProbes({})
    try {
      const res = await gameQueries.fetchModelCatalogDiff()
      if (res.success && res.data) {
        setDiff(res.data)
        setSelection(initSelection(res.data))
        setMessage({ type: 'info', text: res.message })
      }
    }
    catch (e) {
      setMessage({ type: 'error', text: `Sync failed: ${errMsg(e)}` })
    }
    finally {
      setSyncing(false)
    }
  }

  const toggle = (author: string, id: string, checked: boolean) => {
    setSelection((prev) => {
      const set = new Set(prev[author] ?? [])
      if (checked)
        set.add(id)
      else
        set.delete(id)
      return { ...prev, [author]: set }
    })
  }

  const selectedIds = (): string[] => Object.values(selection).flatMap(s => [...s])

  const resetToSuggested = () => {
    if (!diff)
      return
    const sel: Selection = {}
    for (const [author, ids] of Object.entries(diff.suggestedConfig))
      sel[author] = new Set(ids)
    setSelection(sel)
  }

  const handleProbe = async () => {
    const ids = selectedIds()
    if (ids.length === 0)
      return
    setProbing(true)
    setMessage(null)
    try {
      const res = await gameQueries.probeModels(ids)
      if (res.success && res.data) {
        const map: Record<string, ModelProbeReport> = {}
        for (const r of res.data)
          map[r.model] = r
        setProbes(map)
        const failing = res.data.filter(r => !r.ok)
        setMessage(
          failing.length > 0
            ? { type: 'error', text: `${failing.length}/${res.data.length} selected models failed to respond. Deselect them (or they'll be blocked) before applying.` }
            : { type: 'success', text: `All ${res.data.length} selected models responded.` },
        )
      }
    }
    catch (e) {
      setMessage({ type: 'error', text: `Probe failed: ${errMsg(e)}` })
    }
    finally {
      setProbing(false)
    }
  }

  const failingSelected = (): string[] =>
    selectedIds().filter(id => probes[id] && !probes[id]!.ok)

  const deselectFailing = () => {
    const fail = new Set(failingSelected())
    setSelection((prev) => {
      const next: Selection = {}
      for (const [a, s] of Object.entries(prev))
        next[a] = new Set([...s].filter(id => !fail.has(id)))
      return next
    })
  }

  const buildModelConfig = (): Record<string, string[]> => {
    const cfg: Record<string, string[]> = {}
    for (const [author, s] of Object.entries(selection)) {
      const ids = [...s]
      if (ids.length > 0)
        cfg[author] = ids
    }
    return cfg
  }

  const handleApply = async () => {
    setApplying(true)
    setMessage(null)
    setConfirmApply(false)
    try {
      const res = await gameQueries.applyModelConfigUpdate(buildModelConfig(), refreshResponsive)
      if (res.success) {
        setMessage({ type: 'success', text: res.message })
        await handleSync()
      }
    }
    catch (e) {
      setMessage({ type: 'error', text: `Apply failed: ${errMsg(e)}` })
    }
    finally {
      setApplying(false)
    }
  }

  const selectedCount = selectedIds().length
  const failCount = failingSelected().length
  const authorsInOrder = diff ? (diff.authors.filter(a => diff.byAuthor[a]?.length)) : []

  return (
    <div className="bg-slate-50 px-4 py-8 dark:bg-slate-900 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8 text-center">
          <h2 className="mb-2 text-xl font-bold text-foreground md:text-2xl lg:text-3xl">
            Model Catalog Sync
          </h2>
          <p className="text-sm text-muted-foreground">
            Pull the live model list from OpenRouter, review what&apos;s new and what&apos;s been retired,
            probe responsiveness on both the official and OpenRouter paths, then apply a reviewed
            <span className="font-mono"> model_config</span>.
          </p>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <CloudDownload className="size-5" />
                OpenRouter Catalog
                {diff && (
                  <span className="text-xs font-normal text-muted-foreground">
                    ({diff.totalCatalogSize} models live · synced {new Date(diff.fetchedAt).toLocaleString()})
                  </span>
                )}
              </div>
              <Button
                onClick={handleSync}
                disabled={syncing}
                size="sm"
                className="gap-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700"
              >
                {syncing ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
                {syncing ? 'Syncing...' : diff ? 'Re-sync' : 'Sync from Provider'}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {message && (
              <p
                className={`rounded-md px-3 py-2 text-sm ${
                  message.type === 'success'
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : message.type === 'error'
                      ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                }`}
              >
                {message.text}
              </p>
            )}

            {!diff && !syncing && (
              <div className="py-8 text-center text-muted-foreground">
                <p className="mb-2">No catalog loaded.</p>
                <p className="text-sm">Click &quot;Sync from OpenRouter&quot; to fetch the live model list and diff it against the current config.</p>
              </div>
            )}

            {diff && (
              <>
                {/* Summary chips */}
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 font-medium text-green-800 dark:bg-green-900/40 dark:text-green-300">
                    <Sparkles className="size-3" />
                    {diff.toAdd.length} suggested new
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 font-medium text-red-800 dark:bg-red-900/40 dark:text-red-300">
                    <Trash2 className="size-3" />
                    {diff.toRetire.length} retired (auto-removed)
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2.5 py-1 font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                    {diff.kept.length} kept · {selectedCount} selected
                  </span>
                </div>

                {/* Retired panel */}
                {diff.toRetire.length > 0 && (
                  <div className="rounded-lg border border-red-500/30 bg-red-50 p-3 dark:bg-red-900/10">
                    <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-red-700 dark:text-red-400">
                      <AlertTriangle className="size-4" />
                      Retired from OpenRouter — removed from config &amp; pools on apply
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {diff.toRetire.map(id => (
                        <span key={id} className="rounded bg-red-100 px-2 py-0.5 font-mono text-xs text-red-700 line-through dark:bg-red-900/30 dark:text-red-400">
                          {id}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Per-author candidate lists */}
                <div className="space-y-5">
                  {authorsInOrder.map((author) => {
                    const cands = diff.byAuthor[author] ?? []
                    const sel = selection[author] ?? new Set<string>()
                    return (
                      <div key={author} className="rounded-lg border border-border/60 p-3">
                        <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-foreground">
                          {PROVIDER_LABELS[author] ?? author}
                          <span className="ml-2 text-xs font-normal text-muted-foreground">
                            ({sel.size}/{cands.length} selected)
                          </span>
                        </h3>
                        <div className="space-y-1">
                          {cands.map((c) => {
                            const checked = sel.has(c.id)
                            const report = probes[c.id]
                            const failed = report && !report.ok
                            return (
                              <label
                                key={c.id}
                                className={`flex cursor-pointer flex-wrap items-center gap-x-3 gap-y-1 rounded px-2 py-1.5 text-sm hover:bg-muted/50 ${
                                  checked && failed ? 'bg-red-50 dark:bg-red-900/10' : ''
                                }`}
                              >
                                <Checkbox checked={checked} onCheckedChange={ch => toggle(author, c.id, !!ch)} />
                                <span className="font-mono text-xs text-foreground">{shortId(c.id)}</span>
                                {OFFICIAL_PROVIDERS.has(c.author) ? (
                                  <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" title="Served directly by the official provider API">{PROVIDER_LABELS[c.author] ?? c.author}</span>
                                ) : (
                                  <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 dark:bg-violet-900/30 dark:text-violet-400" title="Served via OpenRouter">OpenRouter</span>
                                )}
                                {!c.inCurrentConfig && (
                                  <span className="rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">NEW</span>
                                )}
                                {c.suggested && !c.inCurrentConfig && (
                                  <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">suggested</span>
                                )}
                                {c.isDated && (
                                  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">dated</span>
                                )}
                                {c.isExpiring && (
                                  <span className="rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">retiring</span>
                                )}
                                {c.missingFromOpenRouter && (
                                  <span className="rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-400" title="No longer on OpenRouter — probe its official API before keeping">
                                    not on OR · verify
                                  </span>
                                )}
                                <span className="ml-auto flex items-center gap-2 text-[11px] text-muted-foreground">
                                  {c.contextLength ? `${Math.round(c.contextLength / 1000)}k ctx` : ''}
                                  <span className="font-mono">
                                    {formatPerMillion(c.pricing?.input)}/{formatPerMillion(c.pricing?.output)}
                                  </span>
                                </span>
                                <ProbePill report={report} />
                              </label>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* Action bar */}
                <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
                  <Button onClick={handleProbe} disabled={probing || selectedCount === 0} variant="outline" className="gap-2">
                    {probing ? <Loader2 className="size-4 animate-spin" /> : <Zap className="size-4" />}
                    {probing ? 'Probing...' : `Probe selected (${selectedCount})`}
                  </Button>
                  <Button onClick={resetToSuggested} variant="ghost" size="sm">
                    Reset to suggested
                  </Button>
                  {failCount > 0 && (
                    <Button onClick={deselectFailing} variant="ghost" size="sm" className="text-red-600 dark:text-red-400">
                      Deselect {failCount} failing
                    </Button>
                  )}
                  <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                    <Checkbox checked={refreshResponsive} onCheckedChange={ch => setRefreshResponsive(!!ch)} />
                    Refresh responsive cache after apply (slow)
                  </label>
                  <Button
                    onClick={() => setConfirmApply(true)}
                    disabled={applying || selectedCount === 0 || failCount > 0}
                    className="ml-auto gap-2"
                    title={failCount > 0 ? 'Deselect models failing their primary path first' : undefined}
                  >
                    {applying ? <Loader2 className="size-4 animate-spin" /> : <CheckCircle2 className="size-4" />}
                    {applying ? 'Applying...' : `Apply (${selectedCount} models)`}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Confirm dialog */}
      {confirmApply && diff && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-lg rounded-lg border bg-background p-6">
            <h3 className="mb-3 text-lg font-semibold">Apply model_config?</h3>
            <p className="mb-4 text-sm text-muted-foreground">
              This overwrites the live <span className="font-mono">model_config</span> with
              {' '}
              <strong>{selectedCount}</strong>
              {' '}
              models, removes
              {' '}
              <strong>{diff.toRetire.length}</strong>
              {' '}
              retired models from the config and game pools, and re-syncs pricing.
              {refreshResponsive ? ' The responsive cache will also be recalculated.' : ''}
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setConfirmApply(false)} disabled={applying}>
                Cancel
              </Button>
              <Button onClick={handleApply} disabled={applying} className="gap-2">
                {applying ? <Loader2 className="size-4 animate-spin" /> : null}
                Confirm apply
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
